import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import Modal from "@/components/ui/modal";
import Button from "@/components/ui/button";
import Dropdown from "@/components/ui/dropdown";
import { educationManagementApi } from "@/features/education-management/services/education-management.api";
import type {
  CompleteEducationBlacklist,
  CompleteEducationCandidate,
  CompleteEducationCandidateGroup,
  CompleteEducationDecision,
  CompleteEducationRequest,
} from "@/features/education-management/types/complete-education.types";
import {
  EDUCATION_TYPE_API,
  ExcuseReason,
} from "@/features/education-management/types/education-management.types";

import styles from "./CompleteEducationModal.module.scss";

interface CompleteEducationModalProps {
  isOpen: boolean;
  educationId: string | undefined;
  onClose: () => void;
  onCompleted: () => void;
}

type DecisionMap = Record<string, CompleteEducationDecision>;
type ExcuseMap = Record<string, ExcuseReason | undefined>;

const EXCUSE_REASON_VALUES: ExcuseReason[] = [
  ExcuseReason.HEALTH,
  ExcuseReason.ANNUAL_LEAVE,
  ExcuseReason.BUSINESS_TRAVEL,
  ExcuseReason.OFFICIAL_DUTY,
  ExcuseReason.FAMILY_EMERGENCY,
  ExcuseReason.OTHER,
];

/**
 * Eğitim tamamlama diyaloğu — tam ekran.
 *
 * - Aday katılımcıları GET /complete-candidates üzerinden çeker.
 * - Her satır için yönetici 3 seçenekten birini işaretler:
 *   Tamamladı / Tamamlamadı / Kara listeye al.
 * - Toplu işlem: seçili satırlara tek tıkla karar uygulanır.
 * - Kara listelenen varsa: tek bir ortak "karantina" formu (neden, tarih, açıklama)
 *   gönderilir; backend her kullanıcı için BannedUser kaydı açar.
 */
const CompleteEducationModal: React.FC<CompleteEducationModalProps> = ({
  isOpen,
  educationId,
  onClose,
  onCompleted,
}) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [candidates, setCandidates] = useState<CompleteEducationCandidate[]>([]);
  const [groups, setGroups] = useState<CompleteEducationCandidateGroup[]>([]);
  const [educationType, setEducationType] = useState<number>(
    EDUCATION_TYPE_API.ON_SITE,
  );
  const [decisions, setDecisions] = useState<DecisionMap>({});
  // NOT_COMPLETED kararı için katılımcı başına mazeret nedeni (Madde 10).
  const [excuseReasons, setExcuseReasons] = useState<ExcuseMap>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const getExcuseLabel = useCallback(
    (r: ExcuseReason) =>
      t(`attendanceModal.excuseReasons.${r}`, String(r)),
    [t],
  );

  const excuseReasonOptions = useMemo(
    () =>
      EXCUSE_REASON_VALUES.map((value) => ({
        label: getExcuseLabel(value),
        value: String(value),
      })),
    [getExcuseLabel],
  );

  const [blacklist, setBlacklist] = useState<CompleteEducationBlacklist>({
    reason: 1,
    startDate: "",
    endDate: "",
    description: "",
  });

  useEffect(() => {
    if (!isOpen || !educationId) return;
    setIsLoading(true);
    setSelectedIds(new Set());
    educationManagementApi
      .getCompleteCandidates(educationId)
      .then((res) => {
        const list = Array.isArray(res?.candidates) ? res.candidates : [];
        const groupList = Array.isArray(res?.groups) ? res.groups : [];
        const eduType = res?.educationType ?? EDUCATION_TYPE_API.ON_SITE;
        setEducationType(eduType);
        setCandidates(list);
        setGroups(groupList);
        const initial: DecisionMap = {};
        // Yoklamadan gelen mazeret de ön-doldurulur; aksi halde karar NOT_COMPLETED
        // gelse bile mazeret alanı boş kalır ve form "mazeret zorunlu" diye bloklar.
        const initialExcuses: ExcuseMap = {};
        for (const c of list) {
          if (c.readOnly) continue; // salt-okunur satırlar karar almaz
          const dec = suggestDecision(eduType, c);
          if (dec) initial[c.enrollmentId] = dec;
          if (dec === "NOT_COMPLETED" && c.attendanceExcuseReason != null) {
            initialExcuses[c.enrollmentId] = c.attendanceExcuseReason as ExcuseReason;
          }
        }
        setDecisions(initial);
        setExcuseReasons(initialExcuses);
      })
      .catch(() => {
        toast.error(
          t("completeEducation.loadError"),
        );
      })
      .finally(() => setIsLoading(false));
  }, [isOpen, educationId, t]);

  // Karar verilebilir adaylar (salt-okunur/zaten tamamlamış olanlar hariç). Karar,
  // seçim ve coverage mantığı yalnız bunlar üzerinden işler.
  const decidableCandidates = useMemo(
    () => candidates.filter((c) => !c.readOnly),
    [candidates],
  );

  const counts = useMemo(() => {
    let completed = 0;
    let notCompleted = 0;
    let blacklisted = 0;
    let pending = 0;
    for (const c of decidableCandidates) {
      const d = decisions[c.enrollmentId];
      if (d === "COMPLETED") completed++;
      else if (d === "NOT_COMPLETED") notCompleted++;
      else if (d === "BLACKLISTED") blacklisted++;
      else pending++;
    }
    return { completed, notCompleted, blacklisted, pending };
  }, [decidableCandidates, decisions]);

  const hasBlacklisted = counts.blacklisted > 0;
  // Bekleyen karar yoksa onaylanabilir — herkes zaten tamamladıysa (decidable=0) da
  // eğitim kapatılabilir (backend boş kararla kapatır).
  const allDecided = counts.pending === 0;

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) =>
      prev.size === decidableCandidates.length
        ? new Set()
        : new Set(decidableCandidates.map((c) => c.enrollmentId)),
    );
  };

  const toggleSelectGroup = useCallback(
    (groupCandidates: CompleteEducationCandidate[]) => {
      setSelectedIds((prev) => {
        const ids = groupCandidates.map((c) => c.enrollmentId);
        if (ids.length === 0) return prev;
        const allSelectedInGroup = ids.every((id) => prev.has(id));
        const next = new Set(prev);
        if (allSelectedInGroup) {
          for (const id of ids) next.delete(id);
        } else {
          for (const id of ids) next.add(id);
        }
        return next;
      });
    },
    [],
  );

  const setDecision = (id: string, decision: CompleteEducationDecision) => {
    setDecisions((prev) => ({ ...prev, [id]: decision }));
    if (decision !== "NOT_COMPLETED") {
      setExcuseReasons((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const setExcuseReason = (id: string, value: ExcuseReason | undefined) => {
    setExcuseReasons((prev) => {
      const next = { ...prev };
      if (value == null) {
        delete next[id];
      } else {
        next[id] = value;
      }
      return next;
    });
  };

  const applyBulk = (decision: CompleteEducationDecision) => {
    if (selectedIds.size === 0) {
      toast.error(
        t("completeEducation.bulkNoSelection"),
      );
      return;
    }
    setDecisions((prev) => {
      const next = { ...prev };
      for (const id of selectedIds) next[id] = decision;
      return next;
    });
    if (decision !== "NOT_COMPLETED") {
      setExcuseReasons((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const id of selectedIds) {
          if (id in next) {
            delete next[id];
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }
  };

  const validateBlacklistForm = (): string | null => {
    if (!hasBlacklisted) return null;
    if (!blacklist.startDate || !blacklist.endDate) {
      return t("completeEducation.blacklist.datesRequired");
    }
    if (new Date(blacklist.startDate) >= new Date(blacklist.endDate)) {
      return t("completeEducation.blacklist.dateRangeInvalid");
    }
    return null;
  };

  /** NOT_COMPLETED kararı verilen ama mazereti seçilmemiş satırlar (Madde 10). */
  const missingExcuseCount = useMemo(() => {
    let count = 0;
    for (const c of decidableCandidates) {
      if (
        decisions[c.enrollmentId] === "NOT_COMPLETED" &&
        excuseReasons[c.enrollmentId] == null
      ) {
        count++;
      }
    }
    return count;
  }, [decidableCandidates, decisions, excuseReasons]);

  const handleSubmit = async () => {
    if (!educationId) return;
    if (!allDecided) {
      toast.error(
        t("completeEducation.pendingDecisions"),
      );
      return;
    }
    if (missingExcuseCount > 0) {
      toast.error(
        t(
          "attendance.missingExcuseReason",
          t("attendanceLabels.missingExcuses"),
          { count: missingExcuseCount },
        ),
      );
      return;
    }
    const blacklistError = validateBlacklistForm();
    if (blacklistError) {
      toast.error(blacklistError);
      return;
    }

    const body: CompleteEducationRequest = {
      decisions: decidableCandidates.map((c) => {
        const decision = decisions[c.enrollmentId]!;
        return decision === "NOT_COMPLETED" && excuseReasons[c.enrollmentId] != null
          ? {
              enrollmentId: c.enrollmentId,
              decision,
              excuseReason: excuseReasons[c.enrollmentId]!,
            }
          : {
              enrollmentId: c.enrollmentId,
              decision,
            };
      }),
    };
    if (hasBlacklisted) {
      body.blacklist = {
        reason: blacklist.reason,
        startDate: new Date(blacklist.startDate).toISOString(),
        endDate: new Date(blacklist.endDate).toISOString(),
        description: blacklist.description?.trim() || undefined,
      };
    }

    setIsSubmitting(true);
    try {
      const res = await educationManagementApi.completeEducation(
        educationId,
        body,
      );
      const parts: string[] = [];
      if (res.completedCount > 0) parts.push(`${res.completedCount} tamamladı`);
      if (res.notCompletedCount > 0)
        parts.push(`${res.notCompletedCount} tamamlamadı`);
      if (res.blacklistedCount > 0)
        parts.push(`${res.blacklistedCount} kara listeye alındı`);
      const detail = parts.length ? ` ${parts.join(", ")}.` : "";
      toast.success(`Eğitim tamamlandı.${detail}`);
      onCompleted();
      onClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ??
        t("completeEducation.submitError");
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderSignal = (c: CompleteEducationCandidate) => {
    switch (educationType) {
      case EDUCATION_TYPE_API.UNIT: {
        const pct = typeof c.autoSignal === "number" ? c.autoSignal : 0;
        const fillClass =
          pct >= 80 ? "" : pct >= 40 ? styles.signalFillWarn : styles.signalFillLow;
        return (
          <div className={styles.signalBar}>
            <div className={styles.signalTrack}>
              <div
                className={`${styles.signalFill} ${fillClass}`}
                style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
              />
            </div>
            <span>{Math.round(pct)}%</span>
          </div>
        );
      }
      case EDUCATION_TYPE_API.NONUNIT: {
        const ok = c.autoSignal === true;
        return (
          <span
            className={`${styles.signalBadge} ${ok ? styles.ok : styles.no}`}
          >
            {ok
              ? t("completeEducation.signal.completed")
              : t("completeEducation.signal.notCompleted")}
          </span>
        );
      }
      case EDUCATION_TYPE_API.ONLINE: {
        const ok = c.autoSignal === true;
        const count = c.attendedSessionCount ?? 0;
        return (
          <span
            className={`${styles.signalBadge} ${ok ? styles.ok : styles.no}`}
          >
            {ok
              ? t("completeEducation.signal.joined", {
                  count,
                })
              : t("completeEducation.signal.notJoined")}
          </span>
        );
      }
      case EDUCATION_TYPE_API.ON_SITE:
      default:
        return <span className={styles.signalBadge + " " + styles.neutral}>—</span>;
    }
  };

  const allSelected =
    selectedIds.size === decidableCandidates.length && decidableCandidates.length > 0;

  /**
   * Render edilecek grup listesi. Backend `groups` alanını boş döndürürse
   * (eski deployment, hata durumu) flat aday listesi tek grup olarak fallback
   * eder; böylece modal her zaman bir şey gösterir.
   */
  const renderGroups = useMemo<CompleteEducationCandidateGroup[]>(() => {
    if (groups.length > 0) return groups;
    if (candidates.length === 0) return [];
    return [{ classroomId: null, classroomName: null, candidates }];
  }, [groups, candidates]);

  // Tek "Sınıfsız" grubu varsa (UNIT/NONUNIT veya fallback), grup başlığı gösterme.
  const showGroupHeaders = useMemo(
    () => renderGroups.length > 1 || renderGroups.some((g) => g.classroomId !== null),
    [renderGroups],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="fullscreen"
      title={t("completeEducation.title")}
      loading={isSubmitting}
      customFooter={
        <>
          <Button variant="text" color="secondary" onClick={onClose} type="button">
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="button"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {t("completeEducation.submit")}
          </Button>
        </>
      }
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <h3 className={styles.title}>
              {t("completeEducation.heading")}
            </h3>
            <p className={styles.subtitle}>
              {t("completeEducation.description")}
            </p>
          </div>
          <div className={styles.counts}>
            <span className={`${styles.countPill} ${styles.completed}`}>
              {t("completeEducation.counts.completed", {
                n: counts.completed,
              })}
            </span>
            <span className={`${styles.countPill} ${styles.notCompleted}`}>
              {t("completeEducation.counts.notCompleted", {
                n: counts.notCompleted,
              })}
            </span>
            <span className={`${styles.countPill} ${styles.blacklisted}`}>
              {t("completeEducation.counts.blacklisted", {
                n: counts.blacklisted,
              })}
            </span>
            {counts.pending > 0 && (
              <span className={`${styles.countPill} ${styles.pending}`}>
                {t("completeEducation.counts.pending", {
                  n: counts.pending,
                })}
              </span>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className={styles.loadingState}>
            {t("common.loading")}
          </div>
        ) : candidates.length === 0 ? (
          <div className={styles.emptyState}>
            {t("completeEducation.empty")}
          </div>
        ) : (
          <>
            <div className={styles.toolbar}>
              <span className={styles.toolbarLabel}>
                {t("completeEducation.bulkLabel", { n: selectedIds.size })}
              </span>
              <div className={styles.toolbarActions}>
                <Button
                  variant="outlined"
                  color="success"
                  type="button"
                  onClick={() => applyBulk("COMPLETED")}
                >
                  {t("completeEducation.actions.completed")}
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  type="button"
                  onClick={() => applyBulk("NOT_COMPLETED")}
                >
                  {t("completeEducation.actions.notCompleted")}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  type="button"
                  onClick={() => applyBulk("BLACKLISTED")}
                >
                  {t("completeEducation.actions.blacklisted")}
                </Button>
              </div>
            </div>

            {renderGroups.map((group, groupIdx) => {
              const groupKey = group.classroomId ?? `unassigned-${groupIdx}`;
              const groupCandidates = group.candidates;
              // Seçim/toplu-karar yalnız karar verilebilir (salt-okunur olmayan) satırlarda.
              const groupDecidable = groupCandidates.filter((c) => !c.readOnly);
              const groupAllSelected =
                groupDecidable.length > 0 &&
                groupDecidable.every((c) => selectedIds.has(c.enrollmentId));
              const groupTitleText =
                group.classroomName ?? t("completeEducation.group.unassigned");
              return (
                <div key={groupKey} className={styles.groupBlock}>
                  {showGroupHeaders && (
                    <header className={styles.groupHeader}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={groupAllSelected}
                        onChange={() => toggleSelectGroup(groupDecidable)}
                        aria-label={groupTitleText}
                      />
                      <span className={styles.groupTitle}>{groupTitleText}</span>
                      <span className={styles.groupMeta}>
                        {t("completeEducation.group.count", {
                          n: groupCandidates.length,
                        })}
                      </span>
                    </header>
                  )}
                  <div className={styles.tableWrap}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th style={{ width: 40 }}>
                            <input
                              type="checkbox"
                              className={styles.checkbox}
                              checked={
                                showGroupHeaders ? groupAllSelected : allSelected
                              }
                              onChange={
                                showGroupHeaders
                                  ? () => toggleSelectGroup(groupDecidable)
                                  : toggleSelectAll
                              }
                            />
                          </th>
                          <th>{t("completeEducation.col.name")}</th>
                          <th>{t("completeEducation.col.title")}</th>
                          <th>{t("completeEducation.col.department")}</th>
                          <th>{getSignalColHeader(educationType, t)}</th>
                          <th>{t("completeEducation.col.decision")}</th>
                          <th>{t("attendance.excuseReason")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupCandidates.map((c) => {
                          const d = decisions[c.enrollmentId];
                          return (
                            <tr key={c.enrollmentId}>
                              <td>
                                {c.readOnly ? null : (
                                  <input
                                    type="checkbox"
                                    className={styles.checkbox}
                                    checked={selectedIds.has(c.enrollmentId)}
                                    onChange={() => toggleSelect(c.enrollmentId)}
                                  />
                                )}
                              </td>
                              <td className={styles.nameCell}>
                                {c.firstName} {c.lastName}
                              </td>
                              <td className={styles.metaCell}>{c.title ?? "—"}</td>
                              <td className={styles.metaCell}>
                                {c.departmentName ?? "—"}
                              </td>
                              <td>{renderSignal(c)}</td>
                              <td>
                                {c.readOnly ? (
                                  <span
                                    className={`${styles.signalBadge} ${styles.ok}`}
                                  >
                                    {t("completeEducation.alreadyCompleted")}
                                  </span>
                                ) : (
                                  <div className={styles.segmented}>
                                    <button
                                      type="button"
                                      className={`${styles.segButton} ${
                                        d === "COMPLETED"
                                          ? `${styles.active} ${styles.completed}`
                                          : ""
                                      }`}
                                      onClick={() =>
                                        setDecision(c.enrollmentId, "COMPLETED")
                                      }
                                    >
                                      {t("completeEducation.actions.completed")}
                                    </button>
                                    <button
                                      type="button"
                                      className={`${styles.segButton} ${
                                        d === "NOT_COMPLETED"
                                          ? `${styles.active} ${styles.notCompleted}`
                                          : ""
                                      }`}
                                      onClick={() =>
                                        setDecision(c.enrollmentId, "NOT_COMPLETED")
                                      }
                                    >
                                      {t("completeEducation.actions.notCompleted")}
                                    </button>
                                    <button
                                      type="button"
                                      className={`${styles.segButton} ${
                                        d === "BLACKLISTED"
                                          ? `${styles.active} ${styles.blacklisted}`
                                          : ""
                                      }`}
                                      onClick={() =>
                                        setDecision(c.enrollmentId, "BLACKLISTED")
                                      }
                                    >
                                      {t("completeEducation.actions.blacklisted")}
                                    </button>
                                  </div>
                                )}
                              </td>
                              <td>
                                {d === "NOT_COMPLETED" ? (
                                  (() => {
                                    const excuse = excuseReasons[c.enrollmentId];
                                    const isMissing = excuse == null;
                                    return (
                                      <div className={styles.excuseCell}>
                                        <Dropdown
                                          options={excuseReasonOptions}
                                          value={
                                            excuse != null ? String(excuse) : null
                                          }
                                          selectedLabel={
                                            excuse != null
                                              ? getExcuseLabel(excuse)
                                              : undefined
                                          }
                                          placeholder={t(
                                            "attendance.excuseReasonPlaceholder",
                                          )}
                                          onSelect={(v) => {
                                            const raw = Array.isArray(v) ? v[0] : v;
                                            const num = Number(raw);
                                            setExcuseReason(
                                              c.enrollmentId,
                                              !Number.isNaN(num) &&
                                                num >= 0 &&
                                                num <= 5
                                                ? (num as ExcuseReason)
                                                : undefined,
                                            );
                                          }}
                                        />
                                        {isMissing && (
                                          <span className={styles.excuseError}>
                                            {t("attendance.excuseRequired")}
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })()
                                ) : (
                                  <span className={styles.excuseMuted}>—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}

            {hasBlacklisted && (
              <div className={styles.blacklistSection}>
                <h4 className={styles.blacklistTitle}>
                  {t("completeEducation.blacklist.title", { n: counts.blacklisted })}
                </h4>
                <p className={styles.blacklistHint}>
                  {t("completeEducation.blacklist.hint")}
                </p>
                <div className={styles.blacklistGrid}>
                  <label className={styles.field}>
                    {t("completeEducation.blacklist.reason")}
                    <select
                      value={blacklist.reason}
                      onChange={(e) =>
                        setBlacklist((b) => ({
                          ...b,
                          reason: Number(e.target.value) as 1 | 2,
                        }))
                      }
                    >
                      <option value={1}>
                        {t("completeEducation.blacklist.reasonAbsence")}
                      </option>
                      <option value={2}>
                        {t("completeEducation.blacklist.reasonFailure")}
                      </option>
                    </select>
                  </label>
                  <label className={styles.field}>
                    {t("completeEducation.blacklist.startDate")}
                    <input
                      type="date"
                      value={blacklist.startDate}
                      onChange={(e) =>
                        setBlacklist((b) => ({
                          ...b,
                          startDate: e.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className={styles.field}>
                    {t("completeEducation.blacklist.endDate")}
                    <input
                      type="date"
                      value={blacklist.endDate}
                      onChange={(e) =>
                        setBlacklist((b) => ({
                          ...b,
                          endDate: e.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
                <label className={styles.field}>
                  {t("completeEducation.blacklist.description")}
                  <textarea
                    value={blacklist.description ?? ""}
                    onChange={(e) =>
                      setBlacklist((b) => ({
                        ...b,
                        description: e.target.value,
                      }))
                    }
                    placeholder={t("completeEducation.blacklist.descriptionPlaceholder")}
                  />
                </label>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

/**
 * Varsayılan karar — sadece backend'in güvenle söyleyebildiği durumlarda önerilir.
 *
 * Öncelik SIRALI: kayıtlı yoklama varsa her zaman o kazanır. Sınıfta yoklama
 * alındıktan sonra "Eğitimi Tamamla" denildiğinde yöneticinin aynı bilgiyi ikinci
 * kez girmesinin sebebi buydu — ON_SITE'ta hiçbir sinyal yoktu ve satırlar boş
 * geliyordu.
 *
 * Yoklama yoksa eğitim tipine göre:
 * - UNIT: %100 ise COMPLETED, aksi hâlde NOT_COMPLETED
 * - NONUNIT: contentCompleted=true ise COMPLETED, aksi hâlde NOT_COMPLETED
 * - ONLINE: hasJoined=true ise COMPLETED, aksi hâlde NOT_COMPLETED
 * - ON_SITE: önerme, yönetici seçsin
 *
 * Öneri bağlayıcı değil; yönetici her satırı değiştirebilir.
 */
function suggestDecision(
  educationType: number,
  c: CompleteEducationCandidate,
): CompleteEducationDecision | undefined {
  // Yoklama girilmişse tip fark etmeksizin onu kullan.
  if (c.attendanceDecision === "COMPLETED" || c.attendanceDecision === "NOT_COMPLETED") {
    return c.attendanceDecision;
  }
  switch (educationType) {
    case EDUCATION_TYPE_API.UNIT: {
      const pct = typeof c.autoSignal === "number" ? c.autoSignal : 0;
      return pct >= 100 ? "COMPLETED" : "NOT_COMPLETED";
    }
    case EDUCATION_TYPE_API.NONUNIT:
      return c.autoSignal === true ? "COMPLETED" : "NOT_COMPLETED";
    case EDUCATION_TYPE_API.ONLINE:
      return c.autoSignal === true ? "COMPLETED" : "NOT_COMPLETED";
    default:
      return undefined;
  }
}

function getSignalColHeader(
  type: number,
  t: (k: string) => string,
): string {
  switch (type) {
    case EDUCATION_TYPE_API.UNIT:
      return t("completeEducation.col.progress");
    case EDUCATION_TYPE_API.NONUNIT:
      return t("completeEducation.col.contentStatus");
    case EDUCATION_TYPE_API.ONLINE:
      return t("completeEducation.col.attendance");
    default:
      return t("completeEducation.col.signal");
  }
}

export default CompleteEducationModal;
